import React, { useState } from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Typography,
  Box,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { BlockOutlined } from "@mui/icons-material";
import { ArrowCircleRightOutlined } from "@mui/icons-material";
import { BuildCircleOutlined } from "@mui/icons-material";
const IconMAP = {
  AT: <CheckCircleOutline sx={{ color: "#392AB6", mx: "2px" }} />,
  AWV: <BuildCircleOutlined sx={{ color: "#21CCFB", mx: "2px" }} />,
  MV: <ArrowCircleRightOutlined sx={{ color: "#21CCFB", mx: "2px" }} />,
  NA: <BlockOutlined sx={{ color: "#FC3E30", mx: "2px" }} />,
};

const toInt = (value) => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isInteger(num) ? num : null;
};

const isGlobalRow = (rowData) =>
  rowData?.line === "Global" || rowData?.line === "Global, no LOBs";

const buildInitialAdoptionData = (policyData, row) => {
  const lobList = policyData?.lobAdoptionDecisionList ?? [];

  const getDateString = (date) =>
    date ? dayjs(date).format("YYYY-MM-DD") : "";

  const allSameEffectiveDate =
    lobList.length <= 1 ||
    lobList.every(
      (item) =>
        getDateString(item.effectiveDate) ===
        getDateString(lobList[0]?.effectiveDate)
    );

  const globalEffectiveDate = allSameEffectiveDate
    ? (lobList[0]?.effectiveDate ?? "")
    : "";

  const existingAdoptionDetails = row?.adoptionDetails;
  const hasExistingAdoptionDetails =
    Array.isArray(existingAdoptionDetails) &&
    existingAdoptionDetails.length > 0;
  const hasGlobalAdoption = hasExistingAdoptionDetails
    ? existingAdoptionDetails.some(
        (adoption) => toInt(adoption?.LobConfigKey) === 0
      )
    : false;

  const findAdoptionDetailById = (id) => {
    if (!hasExistingAdoptionDetails) return null;
    return (
      existingAdoptionDetails.find(
        (adoption) => toInt(adoption?.LobConfigKey) === id
      ) ?? null
    );
  };

  const adoptionDataArr = lobList.map((item) => {
    const itemId = toInt(item?.id);
    const existing = itemId != null ? findAdoptionDetailById(itemId) : null;
    const hasExistingEndDate = Boolean(existing?.endDate);
    const toggled = !!existing && !hasExistingEndDate;
    const endDate = existing?.endDate || "";

    return {
      line: item.lobDesc,
      id: item.id,
      toggled,
      date: item.effectiveDate
        ? dayjs(item.effectiveDate).format("MM/DD/YYYY")
        : "",
      endDate,
      variance: item.varianceComments,
      disabled: false,
      adoptionDecisionDesc: item.adoptionDecisionDesc,
      adoptionDecision: item.adoptionDecision,
    };
  });

  const globalAdoptionDetail = findAdoptionDetailById(0);
  const savedGlobalEffectiveDate = globalAdoptionDetail?.effectiveDate
    ? globalAdoptionDetail.effectiveDate
    : null;

  const globalEntry = {
    line: adoptionDataArr.length > 0 ? "Global" : "Global, no LOBs",
    id: "",
    toggled: hasExistingAdoptionDetails ? hasGlobalAdoption : true,
    date: savedGlobalEffectiveDate ?? globalEffectiveDate,
    endDate: "",
    variance: "",
    disabled: !allSameEffectiveDate,
  };

  const initialData = [globalEntry, ...adoptionDataArr].map((rowData, idx) => {
    if (idx === 0) return rowData;
    if (!globalEntry.toggled) return rowData;
    return { ...rowData, disabled: true, toggled: true };
  });

  return initialData;
};

const ApplyAdoptionDecisionsDialog = ({
  editable,
  open,
  onClose,
  policyData,
  //variant,
  handleAdoptionDecisionSave,
  row,
}) => {
  const [adoptionData, setAdoptionData] = useState(() =>
    buildInitialAdoptionData(policyData, row)
  );
  const [globalEffectiveDateError, setGlobalEffectiveDateError] = useState("");

  const hasLobs = (policyData?.lobAdoptionDecisionList?.length ?? 0) > 0;
  const globalRow = adoptionData[0];
  const selectedScope =
    !hasLobs || globalRow?.toggled ? "Global" : "SpecificLOB";
  const isGlobalEditable = editable && (!hasLobs || selectedScope === "Global");
  const shouldValidateGlobalEffectiveDate =
    isGlobalEditable && !globalRow?.disabled;

  const handleSwitchChange = (index) => {
    const newData = [...adoptionData];

    newData[index].toggled = !newData[index].toggled;

    if (!isGlobalRow(newData[index]) && !newData[index].toggled) {
      newData[index].endDate = dayjs().format("YYYY-MM-DD");
    }

    if (!isGlobalRow(newData[index]) && newData[index].toggled) {
      newData[index].endDate = "";
    }

    if (isGlobalRow(newData[index]) && newData[index].toggled) {
      newData.forEach((row, i) => {
        if (i !== index) {
          row.disabled = true;
          row.toggled = true;
        }
      });
    } else if (isGlobalRow(newData[index]) && !newData[index].toggled) {
      newData.forEach((row, i) => {
        if (i !== index) {
          row.disabled = false;
        }
      });
    }

    setAdoptionData(newData);
    setGlobalEffectiveDateError("");
  };

  const handleVarianceChange = (index, value) => {
    const newData = [...adoptionData];
    newData[index].variance = value;
    setAdoptionData(newData);
  };

  const handleEndDateChange = (index, value) => {
    const newData = [...adoptionData];
    newData[index].endDate =
      value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    setAdoptionData(newData);
  };

  const handleGlobalEffectiveDateChange = (value) => {
    const newData = [...adoptionData];
    newData[0].date =
      value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    setAdoptionData(newData);
    setGlobalEffectiveDateError("");
  };

  const handleSave = () => {
    if (shouldValidateGlobalEffectiveDate && !globalRow?.date) {
      setGlobalEffectiveDateError("Global Effective Date is required");
      return;
    }

    setGlobalEffectiveDateError("");
    handleAdoptionDecisionSave(adoptionData, row);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{ fontWeight: "bold", fontSize: "30px", padding: "16px 24px 0px" }}
      >
        Rule Adoption Decisions
      </DialogTitle>
      <Typography
        variant="body2"
        sx={{ padding: "0px 24px 16px", fontSize: "16px" }}
      >
        Rules can be enabled globally (same effective date for all Lines of
        Business) or by available Lines of Business.
      </Typography>
      <DialogContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Line of Business
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Rule Enabled?
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Effective Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                  minWidth: "150px",
                }}
              >
                End Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  padding: "16px 0",
                  fontSize: "16px",
                }}
              >
                Variance Comments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adoptionData.map((rowData, index) => (
              <TableRow
                key={index}
                sx={{
                  opacity: rowData.disabled ? 0.5 : 1, // Dim the row if disabled
                  pointerEvents: rowData.disabled ? "none" : "auto", // Disable interaction if disabled
                }}
              >
                <TableCell align="left" sx={{ padding: "16px 0" }}>
                  <Box display="flex" alignItems="center">
                    {rowData.line} {IconMAP[rowData.adoptionDecision]}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 0" }}>
                  <Switch
                    disabled={
                      rowData.disabled ||
                      !editable ||
                      (!hasLobs && isGlobalRow(rowData))
                    }
                    checked={rowData.toggled}
                    slotProps={{
                      input: {
                        "data-testid": `lob-switch-${rowData.id}`,
                      },
                    }}
                    onChange={() => handleSwitchChange(index)}
                  />
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 0" }}>
                  {isGlobalRow(rowData) ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        disabled={
                          !isGlobalEditable || Boolean(globalRow?.disabled)
                        }
                        value={rowData.date ? dayjs(rowData.date) : null}
                        onChange={handleGlobalEffectiveDateChange}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { width: "100%" },
                            inputProps: {
                              "data-testid": "global-effective-date",
                            },
                            error: Boolean(globalEffectiveDateError),
                            helperText: globalEffectiveDateError,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  ) : rowData.date ? (
                    dayjs(rowData.date).format("MM/DD/YYYY")
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 5px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disabled={
                        isGlobalRow(rowData) ||
                        rowData.disabled ||
                        !editable ||
                        !rowData.toggled
                      }
                      value={rowData.endDate ? dayjs(rowData.endDate) : null}
                      onChange={(newValue) =>
                        handleEndDateChange(index, newValue)
                      }
                      slotProps={{
                        textField: {
                          size: "small",
                          placeholder: "End Date",
                          sx: { width: "100%" },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </TableCell>
                <TableCell align="center" sx={{ padding: "16px 5px" }}>
                  <TextField
                    value={rowData.variance}
                    onChange={(e) =>
                      handleVarianceChange(index, e.target.value)
                    }
                    size="small"
                    placeholder="Variance Text"
                    disabled={rowData.disabled || !editable}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="AAD_Close"
          id="AAD_Close"
          onClick={onClose}
          sx={{ fontWeight: "bold" }}
        >
          Cancel
        </Button>
        <Button
          data-testid="AAD_Save"
          id="AAD_Save"
          onClick={handleSave}
          sx={{ fontWeight: "bold" }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyAdoptionDecisionsDialog;
